import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { LookupOption, LookupTypeCodes } from '../../models/lookup.models';
import { NotesWithMentionsDto } from '../../models/notes.models';
import { ANY_SENIORITY_CODE } from '../../models/request-wizard.models';
import {
  PreferredResourceDto,
  RequestDto,
  RequestedResourceDto,
} from '../../models/request.models';
import { AuthService } from '../../services/auth.service';
import { BreadcrumbService } from '../../services/breadcrumb.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { FloatingDialogService } from '../../services/floating-dialog.service';
import { LookupService } from '../../services/lookup.service';
import { RequestService } from '../../services/request.service';
import { ResourceService } from '../../services/resource.service';
import { SnackbarService } from '../../services/snackbar.service';
import { describeApiError, toApiError } from '../../utils/api-error.utils';
import { BreadcrumbComponent } from '../shared/breadcrumb/breadcrumb.component';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../shared/confirmation-dialog/confirmation-dialog.component';
import { MembershipChipsComponent } from '../shared/membership-chips/membership-chips.component';
import { SelectOption } from '../shared/searchable-multi-select/searchable-multi-select.component';
import {
  RequestWizardDialogComponent,
  RequestWizardDialogData,
} from './request-wizard/request-wizard-dialog.component';

// Interface for assigned resource display
interface AssignedResource {
  id?: number; // AssignedMatterResource.Id (assignedResourceId)
  employeeId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  title?: string;
  email?: string;
  seniorityCode?: string;
  seniority?: string;
  assignmentTypeCategory: string; // PRIMARY or COVERAGE
  assignmentTypeCategoryName: string; // Display name
  status: string; // PENDING, ACTIVE, INACTIVE, CLOSED
  statusName: string; // Display name
  practiceAreaCodes?: string[];
  clientMatterNumbers?: string[]; // Matters this resource is assigned to
  startDate?: Date;
  endDate?: Date;
  canViewResource: boolean;
  notesInfo?: NotesWithMentionsDto;
}

/** A requested seniority and how many of its slots the assigned resources fill. */
interface SeniorityFulfillment {
  seniorityCode: string;
  label: string;
  requested: number;
  fulfilled: number;
}

@Component({
  selector: 'app-request-details',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatDialogModule,
    RouterModule,
    BreadcrumbComponent,
    MembershipChipsComponent,
  ],
  templateUrl: './request-details.component.html',
  styleUrls: ['./request-details.component.scss'],
  animations: [
    // Smoothly grow/shrink the per-matter assignment body when it is toggled
    // open/closed via the @if in the template. Matches the shared
    // time-off-management expandCollapse timing/easing.
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }),
        animate(
          '220ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: '*', opacity: 1 }),
        ),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate(
          '180ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: 0, opacity: 0 }),
        ),
      ]),
    ]),
  ],
})
export class RequestDetailsComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private requestService = inject(RequestService);
  private resourceService = inject(ResourceService);
  private authService = inject(AuthService);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);
  private breadcrumbService = inject(BreadcrumbService);
  private featureFlagService = inject(FeatureFlagService);
  private floatingDialogService = inject(FloatingDialogService);
  private lookupService = inject(LookupService);

  @Input() requestId: number | null = null;
  @Input() floatingDialogId?: string;

  get isDialogMode(): boolean {
    return !!this.floatingDialogId;
  }

  static getMainWindowRoute(inputs: Record<string, unknown>): unknown[] | null {
    const id = inputs?.['requestId'];
    return typeof id === 'number' && id > 0 ? ['/requests', id] : null;
  }

  request: RequestDto | null = null;

  // Built once per load from request.matterDetails (keyed by clientMatterNumber),
  // so the template's per-matter reads don't rebuild the Map every CD cycle.
  matterInfoMap = new Map<
    string,
    { clientName?: string; matterName?: string }
  >();

  isLoading = false;
  error: string | null = null;
  // Set when the API returns 404 for this id — either the request doesn't exist or the user
  // lacks practice-area access (the API deliberately conflates the two). Drives a dedicated
  // "unavailable" placeholder, mirroring the resource detail page.
  accessDenied = false;
  isComparisonPopUpsEnabled = false;

  // Permission check
  canManageRequests = false;
  userPracticeAreas: any[] = [];

  // Practice area code → label lookup (used by getPracticeAreaLabel for display)
  allPracticeAreas: SelectOption[] = [];

  // Assigned resources display (read-only — editing flows through the wizard)
  assignedResources: AssignedResource[] = [];

  // Seniority lookup options, for labelling requested-resource breakdowns.
  seniorityOptions: LookupOption[] = [];

  // Request-level requested-resource fulfilment — computed once per load (and when
  // the seniority lookup arrives), not per change-detection cycle.
  fulfillmentBreakdown: SeniorityFulfillment[] = [];
  fulfilledTotal = 0;
  requestedTotal = 0;

  // Employee-id lookups for O(1) "assigned?" / "preferred?" checks in templates.
  private assignedEmployeeIds = new Set<string>();
  private preferredEmployeeIds = new Set<string>();

  // Per-matter expand/collapse state in the Assignment Details panel.
  expandedMatters = new Set<string>();

  // Per-matter pagination state for the assigned-resources tables.
  private matterPageState = new Map<
    string,
    { pageIndex: number; pageSize: number }
  >();
  readonly matterPageSizeDefault = 10;

  // Request status update state (Mark as Fulfilled)
  isUpdatingStatus = false;

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    // Check permissions
    this.canManageRequests = this.authService.canManageRequests();

    // Get user's practice areas
    this.userPracticeAreas =
      this.authService.getCurrentUserPracticeAreas() || [];

    this.featureFlagService
      .isFeatureFlagEnabled('comparison-pop-ups')
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.isComparisonPopUpsEnabled = enabled;
      });

    // Seniority labels for the requested-resource breakdowns.
    this.lookupService
      .getLookupOptions(LookupTypeCodes.SENIORITY)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (options) => {
          this.seniorityOptions = options;
          // Labels/order depend on the lookup; refresh the breakdown if the
          // request has already loaded.
          this.recomputeFulfillment();
        },
        error: (err) =>
          console.error('Error loading seniority options:', err),
      });

    // Load practice area labels (for getPracticeAreaLabel display lookups)
    this.resourceService
      .getAllPracticeAreas()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (areas) => {
        this.allPracticeAreas = areas.map((pa) => ({
          value: pa.practiceCode,
          label: pa.description,
        }));
      },
      error: (err) => console.error('Error loading practice areas:', err),
    });

    if (this.isDialogMode) {
      if (this.requestId) {
        this.loadRequest();
      } else {
        this.error = 'No request ID provided';
      }
    } else {
      this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
        const id = params['id'];
        if (id) {
          this.requestId = +id;
          this.loadRequest();
        } else {
          this.error = 'No request ID provided';
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadRequest(): void {
    if (!this.requestId) return;

    this.isLoading = true;
    this.error = null;
    this.accessDenied = false;

    this.requestService
      .getRequestById(this.requestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (request) => {
        this.request = request;
        this.breadcrumbService.setDynamicLabel(
          ':requestId',
          'Request #' + request.id,
        );
        this.buildMatterInfoMap();
        this.initializeAssignedResources();
        this.initializeExpandedMatters();
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        const apiError = toApiError(error);
        if (apiError.isNotFound) {
          // Either the request doesn't exist or the user can't access its practice area.
          this.accessDenied = true;
        } else {
          this.error = describeApiError(error, 'Failed to load request details');
        }
      },
    });
  }

  private initializeAssignedResources(): void {
    if (!this.request) return;

    this.assignedResources = (this.request.assignedResourcesWithType ?? []).map(
      (resource) => ({
        id: resource.id,
        employeeId: resource.employeeId,
        firstName: resource.firstName || '',
        lastName: resource.lastName || '',
        fullName:
          resource.fullName ||
          `${resource.firstName || ''} ${resource.lastName || ''}`.trim(),
        title: resource.title,
        email: resource.email,
        seniorityCode: resource.seniorityCode,
        seniority: resource.seniority,
        assignmentTypeCategory: resource.assignmentTypeCategory || 'PRIMARY',
        assignmentTypeCategoryName:
          resource.assignmentTypeCategoryName || 'Primary',
        status: resource.status || 'PENDING',
        statusName: resource.statusName || 'Pending',
        practiceAreaCodes: resource.practiceAreaCodes,
        clientMatterNumbers: resource.clientMatterNumbers,
        startDate: resource.startDate
          ? new Date(resource.startDate)
          : undefined,
        endDate: resource.endDate ? new Date(resource.endDate) : undefined,
        canViewResource: resource.canViewResource ?? false,
        notesInfo: resource.notesInfo,
      }),
    );

    this.recomputeFulfillment();
  }

  retry(): void {
    this.loadRequest();
  }

  goBack(): void {
    this.router.navigate(['/requests']);
  }

  openInFloatingDialog(): void {
    if (!this.request || !this.requestId) {
      return;
    }
    this.floatingDialogService.open(
      RequestDetailsComponent,
      `Request ${this.requestId}`,
      { requestId: this.requestId },
    );
  }

  // ===== REQUEST STATUS MANAGEMENT =====

  onEditRequest(): void {
    if (!this.request?.id) return;
    const dialogRef = this.dialog.open<
      RequestWizardDialogComponent,
      RequestWizardDialogData,
      boolean
    >(RequestWizardDialogComponent, {
      width: '1000px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'new-request-dialog-panel',
      autoFocus: false,
      disableClose: true,
      data: { mode: 'edit', requestId: this.request.id },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((success) => {
        if (success) this.loadRequest();
      });
  }

  markAsFulfilled(): void {
    if (!this.canMarkAsFulfilled() || this.isUpdatingStatus) return;

    const baseMessage =
      'This cannot be undone. Future changes must be made on the Client/Matter directly.';
    const message = this.hasFulfillmentShortfall
      ? `The assigned resources don't satisfy the requested count for every seniority level. You can mark it as fulfilled anyway, but ${baseMessage.charAt(0).toLowerCase()}${baseMessage.slice(1)}`
      : baseMessage;

    const dialogRef = this.dialog.open<
      ConfirmationDialogComponent,
      ConfirmationDialogData,
      boolean
    >(ConfirmationDialogComponent, {
      width: '460px',
      autoFocus: false,
      data: {
        title: 'Mark as Fulfilled?',
        message,
        confirmText: 'Mark as Fulfilled',
        cancelText: 'Cancel',
        cancelVariant: 'destructive',
        icon: 'warning',
        iconColor: '#f59e0b',
        highlightColor: '#fef3c7',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (confirmed) this.performMarkAsFulfilled();
      });
  }

  private performMarkAsFulfilled(): void {
    if (!this.request) return;

    this.isUpdatingStatus = true;

    this.requestService
      .updateRequestStatus(this.request.id, 'FULFILLED')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedRequest) => {
          this.request = updatedRequest;
          this.buildMatterInfoMap();
          this.initializeAssignedResources();
          this.initializeExpandedMatters();
          this.isUpdatingStatus = false;
          this.snackbarService.showSuccess('Request marked as Fulfilled');
        },
        error: (error) => {
          this.isUpdatingStatus = false;
          // The API's sentence (the 409 names which matter is unstaffed, say) wins over our own copy
          this.snackbarService.showError(
            describeApiError(error, 'Failed to mark as Fulfilled'),
          );
        },
      });
  }

  /**
   * True when the assigned resources don't cover the request-level requested
   * count for one or more seniority levels. Drives the non-blocking warning in
   * the Mark-as-Fulfilled confirmation.
   */
  private get hasFulfillmentShortfall(): boolean {
    return this.fulfillmentBreakdown.some((r) => r.fulfilled < r.requested);
  }

  canMarkAsFulfilled(): boolean {
    const hasMatter =
      (this.request?.clientMatterNumbers?.length ?? 0) > 0 ||
      !!this.request?.clientMatterNumber;
    return (
      this.request?.status?.code?.toUpperCase() === 'PENDING' &&
      this.assignedResources.length > 0 &&
      hasMatter &&
      this.getUnstaffedMatters().length === 0
    );
  }

  markAsFulfilledTooltip(): string {
    const status = this.request?.status?.code?.toUpperCase();
    if (status === 'NEW') {
      return 'You cannot fulfill the request without at least one assigned resource';
    }
    if (status !== 'PENDING') return '';
    if (!this.request?.clientMatterNumber) {
      return 'Associate a Client/Matter to this request before marking as Fulfilled';
    }
    if (this.assignedResources.length === 0) {
      return 'Assign at least one resource before marking as Fulfilled';
    }
    const unstaffed = this.getUnstaffedMatters();
    if (unstaffed.length > 0) {
      return `Every Client/Matter must have at least one assigned resource before marking as Fulfilled. Missing: ${unstaffed.join(', ')}`;
    }
    return '';
  }

  /**
   * For a multi-matter request, returns the matters that have no assigned
   * resource yet. Returns [] for single-matter requests (the "at least one
   * resource" check already covers those). Mirrors the backend fulfillment rule.
   */
  private getUnstaffedMatters(): string[] {
    const matters = this.request?.clientMatterNumbers ?? [];
    if (matters.length <= 1) {
      return [];
    }
    const staffed = new Set(
      this.assignedResources.flatMap((r) => r.clientMatterNumbers ?? []),
    );
    return matters.filter((m) => !staffed.has(m));
  }

  canEditRequest(): boolean {
    return (
      this.canManageRequests &&
      this.request?.status?.code?.toUpperCase() !== 'FULFILLED'
    );
  }

  isRequestFulfilled(): boolean {
    return this.request?.status?.code?.toUpperCase() === 'FULFILLED';
  }

  get fulfilledUpdatedInfo(): string | null {
    if (!this.isRequestFulfilled() || !this.request?.updatedAt) {
      return null;
    }

    const date = new Date(this.request.updatedAt);
    if (isNaN(date.getTime())) {
      return null;
    }

    const formattedDate = date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const updaterName = this.request.updatedByUserName?.trim();
    return updaterName
      ? `Fulfilled by ${updaterName} on ${formattedDate}`
      : `Fulfilled on ${formattedDate}`;
  }

  isRequestPending(): boolean {
    return this.request?.status?.code?.toUpperCase() === 'PENDING';
  }


  getStatusClass(status: string | undefined): string {
    const normalizedStatus = status?.toLowerCase() || '';
    switch (normalizedStatus) {
      case 'new':
        return 'badge-new';
      case 'pending':
        return 'badge-pending';
      case 'fulfilled':
        return 'badge-fulfilled';
      default:
        return 'badge-default';
    }
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return 'Not specified';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  getPracticeAreaLabel(code: string): string {
    return this.allPracticeAreas.find((pa) => pa.value === code)?.label ?? code;
  }

  /** Map practice-area codes to display labels for app-membership-chips. */
  getPracticeAreaLabels(codes: string[] | undefined): string[] {
    return (codes ?? []).map((code) => this.getPracticeAreaLabel(code));
  }

  getAssignedResourcePracticeAreas(
    resource: AssignedResource,
  ): { practiceCode: string; description: string }[] {
    return (resource.practiceAreaCodes ?? []).map((code) => ({
      practiceCode: code,
      description: this.getPracticeAreaLabel(code),
    }));
  }

  // ===== Requested-resource fulfilment (request level) =====

  /**
   * Recomputes the request-level fulfilment breakdown and the assigned/preferred
   * id lookups. Called on request load, on assignment change, and when the
   * seniority lookup arrives (labels/order depend on it) — so the template binds
   * to plain fields instead of recomputing on every change-detection cycle.
   */
  private recomputeFulfillment(): void {
    const requested = this.sortBySeniority(
      this.request?.requestedResources ?? [],
    );
    this.requestedTotal = requested.reduce((sum, r) => sum + (r.count || 0), 0);
    this.fulfillmentBreakdown = this.computeFulfillment(
      requested,
      this.assignedResources,
    );
    this.fulfilledTotal = this.fulfillmentBreakdown.reduce(
      (sum, r) => sum + r.fulfilled,
      0,
    );

    this.assignedEmployeeIds = new Set(
      this.assignedResources.map((r) => r.employeeId).filter(Boolean),
    );
    this.preferredEmployeeIds = new Set(
      (this.request?.preferredEmployees ?? [])
        .map((p) => this.getResourceEmployeeId(p))
        .filter((id): id is string => !!id),
    );
  }

  /** Maps a SENIORITY code to its display label ("ANY" => "Any"). */
  seniorityLabel(seniorityCode: string): string {
    if (seniorityCode === ANY_SENIORITY_CODE) return 'Any';
    return (
      this.seniorityOptions.find((o) => o.value === seniorityCode)?.label ??
      seniorityCode
    );
  }

  /** Orders requested rows by the seniority lookup ("Any" first), for display. */
  private sortBySeniority(
    rows: RequestedResourceDto[],
  ): RequestedResourceDto[] {
    const order = (code: string): number => {
      if (code === ANY_SENIORITY_CODE) return -1;
      const idx = this.seniorityOptions.findIndex((o) => o.value === code);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };
    return [...rows].sort(
      (a, b) => order(a.seniorityCode) - order(b.seniorityCode),
    );
  }

  /**
   * Allocates assigned resources to the requested seniority rows: specific-seniority
   * rows fill first by matching seniority, then leftovers (extra/unknown/non-matching)
   * fill the "Any" rows. Each resource counts toward at most one row. Mirrors the
   * wizard assignments-step fulfilment logic.
   */
  private computeFulfillment(
    requested: RequestedResourceDto[],
    assigned: AssignedResource[],
  ): SeniorityFulfillment[] {
    const available = new Map<string, number>();
    for (const a of assigned) {
      const code = a.seniorityCode || '';
      available.set(code, (available.get(code) ?? 0) + 1);
    }

    const rows: SeniorityFulfillment[] = requested.map((r) => ({
      seniorityCode: r.seniorityCode,
      label: this.seniorityLabel(r.seniorityCode),
      requested: r.count,
      fulfilled: 0,
    }));

    // Specific-seniority rows take matching resources first.
    for (const row of rows) {
      if (row.seniorityCode === ANY_SENIORITY_CODE) continue;
      const have = available.get(row.seniorityCode) ?? 0;
      const used = Math.min(row.requested, have);
      row.fulfilled = used;
      available.set(row.seniorityCode, have - used);
    }

    // Everything left over (including unknown seniorities) fills the Any rows.
    let remaining = 0;
    for (const count of available.values()) remaining += count;
    for (const row of rows) {
      if (row.seniorityCode !== ANY_SENIORITY_CODE) continue;
      const used = Math.min(row.requested, remaining);
      row.fulfilled = used;
      remaining -= used;
    }

    return rows;
  }

  // ===== Per-matter assignment grouping (Assignment Details) =====

  /** Rebuilds the clientMatterNumber → matter-detail lookup from the request. */
  private buildMatterInfoMap(): void {
    this.matterInfoMap = new Map(
      (this.request?.matterDetails ?? []).map((d) => [d.clientMatterNumber, d]),
    );
  }

  /** Default-expands a single matter; multi-matter requests start collapsed. */
  private initializeExpandedMatters(): void {
    this.expandedMatters.clear();
    this.matterPageState.clear();
    const matters = this.getDisplayMatters();
    if (matters.length === 1) {
      this.expandedMatters.add(matters[0]);
    }
  }

  toggleMatter(matterNumber: string): void {
    if (this.expandedMatters.has(matterNumber)) {
      this.expandedMatters.delete(matterNumber);
    } else {
      this.expandedMatters.add(matterNumber);
    }
  }

  isMatterExpanded(matterNumber: string): boolean {
    return this.expandedMatters.has(matterNumber);
  }

  /** Assigned resources covering a matter (single-matter requests: all of them). */
  assignedForMatter(matterNumber: string): AssignedResource[] {
    const single = this.getDisplayMatters().length <= 1;
    return this.assignedResources.filter(
      (r) => single || (r.clientMatterNumbers ?? []).includes(matterNumber),
    );
  }

  assignedCountForMatter(matterNumber: string): number {
    return this.assignedForMatter(matterNumber).length;
  }

  /** Per-matter requested counts (by seniority), ordered for display. */
  matterRequestedFor(matterNumber: string): RequestedResourceDto[] {
    return this.sortBySeniority(
      this.request?.matterRequestedResources?.[matterNumber] ?? [],
    );
  }

  /** Compact "1 Any · 2 Junior (0-2 yrs)" summary for a matter header. */
  matterRequestedSummary(matterNumber: string): string {
    return this.matterRequestedFor(matterNumber)
      .filter((r) => (r.count || 0) > 0)
      .map((r) => `${r.count} ${this.seniorityLabel(r.seniorityCode)}`)
      .join(' · ');
  }

  matterPageSize(matterNumber: string): number {
    return (
      this.matterPageState.get(matterNumber)?.pageSize ??
      this.matterPageSizeDefault
    );
  }

  matterPageIndex(matterNumber: string): number {
    return this.matterPageState.get(matterNumber)?.pageIndex ?? 0;
  }

  pagedAssignedForMatter(matterNumber: string): AssignedResource[] {
    const all = this.sortResourcesByOperationOrder(
      this.assignedForMatter(matterNumber),
    );
    const size = this.matterPageSize(matterNumber);
    const start = this.matterPageIndex(matterNumber) * size;
    return all.slice(start, start + size);
  }

  onMatterPageChange(matterNumber: string, event: PageEvent): void {
    this.matterPageState.set(matterNumber, {
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
    });
  }

  getAssignmentLengthInWeeks(): number | null {
    if (!this.request?.startDate || !this.request?.endDate) return null;
    const start = new Date(this.request.startDate);
    const end = new Date(this.request.endDate);
    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    return Math.ceil(diffDays / 7);
  }

  formatRequestType(requestType: string | undefined): string {
    if (!requestType) return '';
    // Add space before capital letters (except the first one)
    return requestType.replace(/([A-Z])/g, ' $1').trim();
  }

  getRequestorInitials(name: string): string {
    if (!name) return '?';
    const names = name.trim().split(' ');
    if (names.length === 0) return '?';
    const firstInitial = names[0].charAt(0).toUpperCase();
    const lastInitial =
      names.length > 1 ? names[names.length - 1].charAt(0).toUpperCase() : '';
    return firstInitial + lastInitial;
  }

  getResourceInitials(resource: any): string {
    // Handle different property name formats
    const firstName = resource?.firstName || resource?.FirstName || '';
    const lastName = resource?.lastName || resource?.LastName || '';
    const name = resource?.fullName || resource?.name || '';

    if (firstName || lastName) {
      const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
      const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
      return firstInitial + lastInitial;
    }

    if (!name) return '?';
    const names = name.trim().split(' ');
    if (names.length === 0) return '?';
    const firstInitial = names[0].charAt(0).toUpperCase();
    const lastInitial =
      names.length > 1 ? names[names.length - 1].charAt(0).toUpperCase() : '';
    return firstInitial + lastInitial;
  }

  getResourceName(resource: any): string {
    // Handle different property name formats from the API
    const firstName = resource?.firstName || resource?.FirstName || '';
    const lastName = resource?.lastName || resource?.LastName || '';

    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }

    return resource?.fullName || resource?.name || 'Unknown Resource';
  }

  getResourceEmployeeId(resource: any): string | null {
    return (
      resource?.id ||
      resource?.Id ||
      resource?.employeeId ||
      resource?.employeeID ||
      null
    );
  }

  /** True when a preferred resource has been assigned to a matter (in the assigned list). */
  isPreferredAssigned(resource: PreferredResourceDto): boolean {
    const id = this.getResourceEmployeeId(resource);
    return !!id && this.assignedEmployeeIds.has(id);
  }

  /** True when an assigned resource is also on the preferred list (drives the "(preferred)" tag). */
  isAssignedResourcePreferred(resource: AssignedResource): boolean {
    return this.preferredEmployeeIds.has(resource.employeeId);
  }

  /** Joins a title and seniority into a "Title · Seniority" subline, omitting blanks. */
  resourceSubline(title?: string | null, seniority?: string | null): string {
    return [title, seniority].filter(Boolean).join(' · ');
  }

  getMatterId(): string | null {
    return this.request?.clientMatterNumbers?.[0] ?? this.request?.clientMatterNumber ?? null;
  }

  getDisplayMatters(): string[] {
    if (this.request?.clientMatterNumbers && this.request.clientMatterNumbers.length > 0) {
      return this.request.clientMatterNumbers;
    }
    if (this.request?.clientMatterNumber) {
      return [this.request.clientMatterNumber];
    }
    return [];
  }

  navigateToMatter(): void {
    const matterId = this.getMatterId();
    if (matterId) {
      this.navigateToMatterById(matterId);
    }
  }

  navigateToMatterById(matterId: string): void {
    if (matterId) {
      const currentCrumbs = this.breadcrumbService.getCurrentBreadcrumbs();
      this.router.navigate(['/matters', matterId], {
        state: {
          breadcrumbOverride: [
            ...currentCrumbs,
            { label: ':matterId', url: '' },
          ],
        },
      });
    }
  }

  navigateToResource(employeeId: string): void {
    if (employeeId) {
      const currentCrumbs = this.breadcrumbService.getCurrentBreadcrumbs();
      this.router.navigate(['/resources', employeeId], {
        state: {
          breadcrumbOverride: [
            ...currentCrumbs,
            { label: ':employeeId', url: '' },
          ],
        },
      });
    }
  }

  /** Sorts assigned resources by last name for a stable display order. */
  private sortResourcesByOperationOrder(
    resources: AssignedResource[],
  ): AssignedResource[] {
    return [...resources].sort((a, b) => a.lastName.localeCompare(b.lastName));
  }
}
